import { useEffect, useState } from "react";
import styled from "styled-components";
import Button from "../../../components/controls/button";
import { ColumnConfig, FetchParams } from "../../../components/controls/table-view";
import TableView from "../../../components/controls/table-view";
import { ICON_ID } from "../../../components/icons/google-icon";
import { showError, showSuccess } from "../../../components/notification/toastr-actions";
import { useAppDispatch } from "../../../hooks/useAppDispatch";
import { useAppSelector } from "../../../hooks/useAppSelector";
import { TextContainer } from "../../../styles/shared/table";
import {
    fetchSupplierOrders,
    generateSupplierOrderPDF,
    resetSupplierOrders,
} from "../../../store/features/supplier/supplier-order-slice";

interface SupplierOrdersProps {
    supplierId: number;
    supplierName?: string;
}

export default function SupplierOrders({ supplierId, supplierName }: SupplierOrdersProps) {
    const dispatch = useAppDispatch();
    const supplierOrders = useAppSelector((state) => state.supplierOrders);
    const [activeOrderId, setActiveOrderId] = useState<number | null>(null);

    useEffect(() => {
        fetchMoreData({ reset: true });

        return () => {
            dispatch(resetSupplierOrders());
        };
    }, [supplierId]);

    const fetchMoreData = async (fetchParams: FetchParams) => {
        await dispatch(fetchSupplierOrders({ supplierId, fetchParams }));
    };

    const buildPdfFileName = (orderId: number) => {
        const safeSupplierName = (supplierName ?? "Supplier").replace(/[^a-zA-Z0-9_-]/g, "-");
        return `${safeSupplierName}-Order-${orderId}.pdf`;
    };

    const downloadPdf = (pdfBlob: Blob, fileName: string) => {
        const pdfUrl = window.URL.createObjectURL(pdfBlob);
        const newWindow = window.open(pdfUrl);
        if (!newWindow) {
            console.error("Failed to open new window");
        }

        const link = document.createElement("a");
        link.href = pdfUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
            window.URL.revokeObjectURL(pdfUrl);
        }, 1000);
    };

    const handleDownload = async (orderId: number) => {
        setActiveOrderId(orderId);

        const resultAction = await dispatch(generateSupplierOrderPDF({ supplierId, orderId }));
        setActiveOrderId(null);

        if (!generateSupplierOrderPDF.fulfilled.match(resultAction)) {
            dispatch(showError(`Failed to download PDF for order #${orderId}`));
            return;
        }

        const fileName = buildPdfFileName(orderId);
        downloadPdf(resultAction.payload, fileName);
    };

    const handleShare = async (orderId: number) => {
        setActiveOrderId(orderId);

        const resultAction = await dispatch(generateSupplierOrderPDF({ supplierId, orderId }));
        setActiveOrderId(null);

        if (!generateSupplierOrderPDF.fulfilled.match(resultAction)) {
            dispatch(showError(`Failed to share PDF for order #${orderId}`));
            return;
        }

        const fileName = buildPdfFileName(orderId);
        const pdfBlob = resultAction.payload;

        const canShareFiles =
            typeof navigator !== "undefined" &&
            typeof navigator.share === "function" &&
            typeof (navigator as any).canShare === "function";

        if (canShareFiles) {
            try {
                const file = new File([pdfBlob], fileName, { type: "application/pdf" });
                const sharePayload = {
                    title: `Order #${orderId}`,
                    text: `Supplier order #${orderId}`,
                    files: [file],
                };

                if ((navigator as any).canShare({ files: [file] })) {
                    await navigator.share(sharePayload);
                    return;
                }
            } catch {
                // Fall through to download fallback.
            }
        }

        downloadPdf(pdfBlob, fileName);
        dispatch(showSuccess("Sharing is not supported on this device, PDF downloaded instead"));
    };

    const renderActionButton = (row: any) => {
        const isLoading = activeOrderId === row.id;

        return (
            <ActionContainer>
                <Button
                    variant="outlined"
                    id={`btnShareOrder_${row.id}`}
                    label="Share"
                    icon={ICON_ID.Share}
                    onClick={() => handleShare(row.id)}
                    loading={isLoading}
                    fullWidth={false}
                />
                <Button
                    variant="contained"
                    id={`btnDownloadOrder_${row.id}`}
                    label="Download"
                    icon={ICON_ID.Save}
                    onClick={() => handleDownload(row.id)}
                    loading={isLoading}
                    fullWidth={false}
                />
            </ActionContainer>
        );
    };

    const columnConfig: { [key: string]: ColumnConfig } = {
        id: {
            order: 1,
            name: "Order #",
            render: (value: number) => <TextContainer>{value}</TextContainer>,
        },
        itemCount: {
            order: 2,
            name: "Items",
            render: (value: number) => <TextContainer>{value}</TextContainer>,
        },
        createdDate: {
            order: 3,
            name: "Created",
            render: (value: Date) => {
                const created = new Date(value);
                return (
                    <TextContainer>
                        {created.toLocaleDateString()} {created.toLocaleTimeString()}
                    </TextContainer>
                );
            },
        },
    };

    return (
        <TableContainer>
            <TableView
                id="tblSupplierOrders"
                emptyText="No orders found"
                preLoadedData={true}
                fetchData={(params) => fetchMoreData(params)}
                totalCount={supplierOrders.orders?.count ?? 0}
                data={supplierOrders.orders?.orders ?? []}
                columnConfig={columnConfig}
                actionButton={renderActionButton}
                showHeaders={true}
            />
        </TableContainer>
    );
}

const TableContainer = styled.div`
  min-height: 0;
`;

const ActionContainer = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;